// Text-only course transport. It deliberately owns no audio or media resources:
// course chat streams over the gateway's existing realtime event protocol.
import { isArchiveError, ARCHIVE_DISCONNECTED_MESSAGE } from "./courseArchive.js";
import { createCourseHistoryReceiver } from './courseHistory.js';

function defaultUrl() {
  const current = typeof location === "undefined" ? { protocol: "http:", host: "localhost" } : location;
  const proto = current.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${current.host}/v1/realtime?realtime_mode=chat`;
}

export class TextCourseClient {
  constructor({ model, courseId, thinking = true, handlers = {}, WebSocketImpl = WebSocket, url } = {}) {
    this.model = model;
    this.courseId = courseId;
    this.thinking = thinking;
    this.handlers = handlers;
    this.historyReceiver = createCourseHistoryReceiver(handlers.onHistoryMessage);
    this.WebSocketImpl = WebSocketImpl;
    this.url = url || defaultUrl();
    this.socket = null;
    this.closed = false;
    this.sessionStarted = false;
    this.openerComplete = false;
    this.generating = false;
    this.archiving = false;
    this.archivePending = false;
    this.archiveQueued = false;
    this.transcripts = new Map();
  }

  get canSend() {
    return !this.closed && this.socket?.readyState === 1 && this.sessionStarted &&
      this.openerComplete && !this.generating && !this.archiving && !this.archivePending;
  }

  _status(status) {
    if (this.archivePending && ["ready", "opening", "thinking", "speaking"].includes(status)) {
      status = this.archiving ? "archiving" : "archive_retry";
    }
    this.handlers.onStatus?.(status);
  }
  _error(message) { this.handlers.onError?.(message); }
  _agent(kind, data) { this.handlers.onAgentUpdate?.(kind, data); }

  connect() {
    if (this.socket && !this.closed) return false;
    this.closed = false;
    this.sessionStarted = false;
    this.openerComplete = false;
    this.generating = false;
    this.archiving = false;
    this.archivePending = false;
    this.transcripts.clear();
    this.archiveQueued = false;
    this.historyReceiver.clear();
    this._status("connecting");
    const socket = new this.WebSocketImpl(this.url);
    this.socket = socket;
    socket.onopen = () => {
      if (this.closed || this.socket !== socket) return;
      socket.send(JSON.stringify({
        type: "session.update",
        session: {
          model: this.model,
          course_id: this.courseId,
          modalities: ["text"],
          thinking: this.thinking,
        },
      }));
      this._status("opening");
    };
    socket.onmessage = (event) => {
      if (this.closed || this.socket !== socket) return;
      try { this._handle(JSON.parse(event.data)); }
      catch { this._error("Invalid websocket message"); }
    };
    socket.onerror = () => {
      if (!this.closed && this.socket === socket) this._error("websocket error");
    };
    socket.onclose = () => {
      if (!this.closed && this.socket === socket) {
        this.generating = false;
        if (this.archivePending && !this.archiveQueued) this._error(ARCHIVE_DISCONNECTED_MESSAGE);
        this._status(this.archivePending ? "archive_disconnected" : "closed");
      }
    };
    return true;
  }

  sendText(text) {
    const value = (text || "").trim();
    if (!value || !this.canSend) return false;
    this.generating = true;
    this.socket.send(JSON.stringify({ type: "input_text", text: value }));
    this._status("thinking");
    return true;
  }

  setThinking(value) {
    this.thinking = value !== false;
    if (this.socket?.readyState === 1 && !this.closed) this.socket.send(JSON.stringify({
      type: "session.update", session: { model: this.model, course_id: this.courseId, modalities: ["text"], thinking: this.thinking },
    }));
  }

  finishCourse() { return this._control("course.finish"); }
  interruptCourse() { return this._control("course.interrupt"); }

  _control(type) {
    if (this.closed || this.socket?.readyState !== 1 || !this.sessionStarted || this.archiving) return false;
    if (type === "course.interrupt" && this.archivePending) return false;
    if (type === "course.finish") this.archivePending = true;
    this.archiving = true;
    this.socket.send(JSON.stringify({ type }));
    this._status("archiving");
    return true;
  }

  stop() {
    if (this.closed) return;
    this.closed = true;
    this.generating = false;
    this.archiving = false;
    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState < 2) socket.close();
  }

  _handle(event) {
    switch (event.type) {
      case "agent.update":
        if (event.kind === 'session.history') this.historyReceiver.push(event.data);
        if (event.kind === "session.start") {
          this.sessionStarted = true;
          this.openerComplete = event.data?.resumed === true || event.data?.counselor_speaks_first === false;
          this._status(this.openerComplete ? "ready" : "opening");
        }
        if (event.kind === "session.finishing") {
          this.archivePending = true;
          this.archiving = true;
          this._status("archiving");
        }
        if (event.kind === "session.archive_queued") this.archiveQueued = true;
        if (event.kind === "session.finish" || event.kind === "session.interrupt") {
          this.sessionStarted = false;
          this.openerComplete = false;
          this.generating = false;
          this.archiving = false;
          this.archivePending = false;
          this._status(event.kind === "session.finish" ? "finished" : "interrupted");
        }
        this._agent(event.kind, event.data || {});
        break;
      case "response.created":
        this.generating = true;
        this._status("thinking");
        break;
      case "response.reasoning.started":
        this.handlers.onReasoning?.("", false, event.response_id);
        break;
      case "response.reasoning.delta": {
        const id = event.response_id || "response";
        const key = `reasoning:${id}`;
        const text = (this.transcripts.get(key) || "") + (event.delta || "");
        this.transcripts.set(key, text);
        this.handlers.onReasoning?.(text, false, id);
        break;
      }
      case "response.reasoning.done":
        this.handlers.onReasoning?.(event.reasoning || this.transcripts.get(`reasoning:${event.response_id}`) || "", true, event.response_id);
        break;
      case "response.audio_transcript.delta": {
        const id = event.response_id || "response";
        const key = `answer:${id}`;
        const text = (this.transcripts.get(key) || "") + (event.delta || "");
        this.transcripts.set(key, text);
        this._status("speaking");
        this.handlers.onAssistantText?.(text, false, id);
        break;
      }
      case "response.audio_transcript.done": {
        const id = event.response_id || "response";
        const text = event.transcript ?? this.transcripts.get(`answer:${id}`) ?? "";
        this.transcripts.set(`answer:${id}`, text);
        this.handlers.onAssistantText?.(text, true, id);
        break;
      }
      case "response.done":
        this.generating = false;
        this.openerComplete = true;
        if (!this.archiving) this._status(this.sessionStarted ? "ready" : "opening");
        break;
      case "error":
        this.generating = false;
        const archiveError = this.archiving;
        this.archiving = false;
        if (isArchiveError(event.error)) this.archivePending = true;
        if (event.error?.type === "empty_session") this.archivePending = false;
        this._error(event.error?.message || "server error");
        this._status(this.archivePending ? "archive_retry"
          : this.sessionStarted && (this.openerComplete || archiveError) ? "ready" : "error");
        break;
      default:
        break;
    }
  }
}
